import {
  ChevronRight,
  FileText,
  Folder as FolderIcon,
  FolderOpen,
  Globe,
  Layers,
  PenLine,
  Sparkles,
} from "lucide-react";
import { useCallback, useMemo, useState, type DragEvent, type ReactNode } from "react";

import {
  CATEGORY_LABEL,
  type FolderItemOut,
  type FolderOut,
  type NoteCategory,
  type SharedFolderOut,
} from "@/lib/api";
import { CATEGORY_COLOR, folderColor } from "@/lib/palette";
import { cn } from "@/lib/utils";

import {
  applyClick,
  refKey,
  refsForGesture,
  toFileRef,
  useFileSelection,
  type FileRef,
} from "./file-selection";
import { useFolderUpload } from "./use-folder-upload";
import { canMoveInto, movingRefs, type MoveTarget } from "./use-move-items";

/** The drag payload type. A private mime keeps a file drag from being confused
 * with an OS file drop (which `GlobalDropzone` handles) — the two look identical
 * to a drop handler otherwise, and uploading a note you meant to move would be a
 * memorable bug. */
export const FILE_DND_TYPE = "application/x-graphmind-files";

/** `items` / `children` are absent when the tree was fetched WITHOUT items,
 * and can be absent from a server that predates them — a real case for a PWA,
 * whose bundle is cached and can meet an older or newer API than it was built
 * against. Reading them through these keeps a version skew from taking the
 * whole shell down with an "is not iterable". */
const itemsOf = (folder: FolderOut): FolderItemOut[] => folder.items ?? [];
const childrenOf = (folder: FolderOut): FolderOut[] => folder.children ?? [];

const ITEM_ICON: Record<string, typeof FileText> = {
  handwritten: PenLine,
  website: Globe,
};

function itemIcon(item: FolderItemOut) {
  if (item.node_type === "ai_output") return Sparkles;
  return ITEM_ICON[item.category] ?? FileText;
}

/** A flattened, ordered row list — what a Shift-range and keyboard navigation
 * measure against. Folders are excluded: a range selection over files is what
 * every explorer means by it, and mixing folders in makes Shift+click select
 * things the user cannot see the extent of. */
export function flattenVisible(
  roots: FolderOut[],
  isOpen: (category: NoteCategory, path: string) => boolean,
): FileRef[] {
  const out: FileRef[] = [];
  const walk = (folder: FolderOut) => {
    if (!isOpen(folder.category, folder.path)) return;
    for (const child of childrenOf(folder)) walk(child);
    for (const item of itemsOf(folder)) out.push(toFileRef(item, folder.path));
  };
  for (const root of roots) walk(root);
  return out;
}

export interface FileTreeCallbacks {
  onOpen: (item: FolderItemOut) => void;
  onDropInto: (refs: FileRef[], target: MoveTarget) => void;
  onFolderContextMenu?: (folder: FolderOut, event: { x: number; y: number }) => void;
  onItemContextMenu?: (refs: FileRef[], event: { x: number; y: number }) => void;
  /** Rendered at the end of a folder row on hover — "new subfolder" and friends. */
  folderActions?: (folder: FolderOut) => ReactNode;
  /** The same, for a PROJECT row. Separate because a project has no single
   * category folder behind it, so the per-category actions don't apply. */
  sharedActions?: (folder: SharedFolderOut) => ReactNode;
}

/**
 * The file tree, shared verbatim by the left rail and Folder View.
 *
 * Structurally this replaces two near-identical trees that had drifted: the rail
 * mirrored the DISK (`Papers/` + `Markdown/`) while everything else showed the
 * four product categories, so the same note appeared under different parents
 * depending on where you looked, and a file dragged in one place meant nothing
 * in the other. One component over one tree is the fix.
 *
 * Ergonomically it adds what a file tree is expected to have and this one didn't:
 * multi-select (plain / Ctrl / Shift), dragging a whole selection at once, drop
 * targets that refuse impossible moves DURING the drag rather than after, and a
 * context menu on every row.
 */
export function FileTree({
  roots,
  shared,
  callbacks,
  className,
  defaultOpenDepth = 1,
}: {
  roots: FolderOut[];
  /** Folders seen ACROSS categories, shown above them. They are just folders;
   *  "project" was an invented word for the same thing. */
  shared?: SharedFolderOut[];
  callbacks: FileTreeCallbacks;
  className?: string;
  defaultOpenDepth?: number;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const keyFor = (category: NoteCategory, path: string) => `${category}:${path}`;
  const isOpen = useCallback(
    (category: NoteCategory, path: string) =>
      open[keyFor(category, path)] ?? path.split("/").filter(Boolean).length < defaultOpenDepth,
    [open, defaultOpenDepth],
  );
  const toggle = (category: NoteCategory, path: string) =>
    setOpen((prev) => ({
      ...prev,
      [keyFor(category, path)]: !isOpen(category, path),
    }));

  const ordered = useMemo(() => flattenVisible(roots, isOpen), [roots, isOpen]);

  return (
    <div
      // `tree` + `treeitem`, not `listbox`/`option`: this IS a tree, and an
      // `option` outside a listbox is invalid ARIA that a screen reader has no
      // way to render sensibly. `aria-multiselectable` is what tells assistive
      // tech that Ctrl/Shift ranges are available here at all.
      role="tree"
      aria-label="Files"
      aria-multiselectable="true"
      className={cn("select-none", className)}
      // Clicking the empty space below the tree clears the selection, the way
      // clicking empty space in any file manager does.
      onClick={(e) => {
        if (e.target === e.currentTarget) useFileSelection.getState().clear();
      }}
    >
      {!!shared?.length && (
        <>
          <p className="px-1 pb-0.5 pt-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground/70">
            Folders
          </p>
          {shared.map((project) => (
            <SharedFolderNode
              key={project.path}
              folder={project}
              depth={0}
              ordered={ordered}
              callbacks={callbacks}
            />
          ))}
          <div className="my-1 h-px bg-border/60" />
        </>
      )}
      {roots.map((root) => (
        <FolderNode
          key={`${root.category}:${root.path}`}
          folder={root}
          depth={0}
          isOpen={isOpen}
          onToggle={toggle}
          ordered={ordered}
          callbacks={callbacks}
        />
      ))}
    </div>
  );
}

/**
 * A PROJECT folder — one name across every category it appears in.
 *
 * "Research" holding its markdown notes AND its handwriting AND its papers is
 * one folder viewed from several categories, not several folders that happen to
 * share a name. Nothing is duplicated to make that true: these rows render the
 * SAME items the category subtrees render, which is why dragging a file out of a
 * project and dragging it out of `Markdown / Research` are the same act.
 *
 * A drop here carries no destination category, so a MIXED selection is legal —
 * each item keeps its own category and only its folder changes.
 */
function SharedFolderNode({
  folder,
  depth,
  ordered,
  callbacks,
}: {
  folder: SharedFolderOut;
  depth: number;
  ordered: FileRef[];
  callbacks: FileTreeCallbacks;
}) {
  const [open, setOpen] = useState(depth === 0);
  const [dropState, setDropState] = useState<"none" | "ok">("none");
  const target: MoveTarget = { category: null, folder: folder.path };
  const native = useFolderUpload({ folder: folder.path, label: folder.name });
  // The union of every facet's items — the same files, gathered.
  const items = (folder.facets ?? []).flatMap((facet) => facet.items ?? []);
  const empty = (folder.children ?? []).length === 0 && items.length === 0;

  return (
    <div role="treeitem" aria-expanded={empty ? undefined : open} aria-level={depth + 1}>
      <div
        className={cn(
          "group/folder relative flex items-center gap-1 rounded pr-1 transition-colors",
          (dropState === "ok" || native.over) && "bg-primary/20 ring-1 ring-primary/50",
          dropState === "none" && !native.over && "hover:bg-accent/60",
        )}
        style={{ paddingLeft: `${depth * 11}px` }}
        onDragOver={(e) => {
          native.props.onDragOver(e);
          if (!e.dataTransfer.types.includes(FILE_DND_TYPE)) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = "move";
          if (dropState === "none") setDropState("ok");
        }}
        onDragEnter={(e) => {
          native.props.onDragEnter(e);
          if (e.dataTransfer.types.includes(FILE_DND_TYPE)) setDropState("ok");
        }}
        onDragLeave={(e) => {
          native.props.onDragLeave(e);
          if (e.currentTarget.contains(e.relatedTarget as Node)) return;
          setDropState("none");
        }}
        onDrop={(e) => {
          native.props.onDrop(e);
          if (!e.dataTransfer.types.includes(FILE_DND_TYPE)) return;
          e.preventDefault();
          e.stopPropagation();
          setDropState("none");
          try {
            const refs = JSON.parse(e.dataTransfer.getData(FILE_DND_TYPE) || "[]") as FileRef[];
            const moving = movingRefs(refs, target);
            if (moving.length) callbacks.onDropInto(moving, target);
          } catch {
            /* a malformed payload is not a move */
          }
        }}
      >
        <button
          type="button"
          aria-label={`${open ? "Collapse" : "Expand"} ${folder.name}`}
          disabled={empty}
          onClick={() => setOpen((v) => !v)}
          className="grid h-6 w-4 shrink-0 place-items-center text-muted-foreground disabled:opacity-0"
        >
          <ChevronRight className={cn("h-3 w-3 transition-transform", open && "rotate-90")} />
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-1.5 py-1 text-left"
        >
          <Layers className="h-3.5 w-3.5 shrink-0" style={{ color: folderColor(folder.color) }} />
          <span className="truncate text-[12.5px] text-foreground/75">{folder.name}</span>
          <span className="ml-auto shrink-0 pl-2 font-mono text-[9px] text-muted-foreground/70">
            {folder.total_count || ""}
          </span>
        </button>
        {callbacks.sharedActions?.(folder)}
      </div>

      {open && (
        <div role="group">
          {(folder.children ?? []).map((child) => (
            <SharedFolderNode
              key={child.path}
              folder={child}
              depth={depth + 1}
              ordered={ordered}
              callbacks={callbacks}
            />
          ))}
          {items.map((item) => (
            <FileRow
              key={`shared:${refKey(item)}`}
              item={item}
              folder={folder.path}
              depth={depth + 1}
              ordered={ordered}
              callbacks={callbacks}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FolderNode({
  folder,
  depth,
  isOpen,
  onToggle,
  ordered,
  callbacks,
}: {
  folder: FolderOut;
  depth: number;
  isOpen: (category: NoteCategory, path: string) => boolean;
  onToggle: (category: NoteCategory, path: string) => void;
  ordered: FileRef[];
  callbacks: FileTreeCallbacks;
}) {
  const [dropState, setDropState] = useState<"none" | "ok" | "blocked">("none");
  const open = isOpen(folder.category, folder.path);
  const isRoot = folder.path === "";
  const target: MoveTarget = { category: folder.category, folder: folder.path };
  const empty = childrenOf(folder).length === 0 && itemsOf(folder).length === 0;
  // Dropping files from the DESKTOP here uploads them into this folder, as
  // distinct from dragging a note WITHIN the app, which moves it.
  const native = useFolderUpload({
    folder: folder.path,
    label: isRoot ? CATEGORY_LABEL[folder.category] : folder.name,
    category: folder.category,
  });

  const readDrag = (e: DragEvent): FileRef[] => {
    try {
      const raw = e.dataTransfer.getData(FILE_DND_TYPE);
      return raw ? (JSON.parse(raw) as FileRef[]) : [];
    } catch {
      return [];
    }
  };

  return (
    <div role="treeitem" aria-expanded={empty ? undefined : open} aria-level={depth + 1}>
      <div
        className={cn(
          "group/folder relative flex items-center gap-1 rounded pr-1 transition-colors",
          (dropState === "ok" || native.over) && "bg-primary/20 ring-1 ring-primary/50",
          dropState === "blocked" && "bg-destructive/10 ring-1 ring-destructive/40",
          dropState === "none" && !native.over && "hover:bg-accent/60",
        )}
        style={{ paddingLeft: `${depth * 11}px` }}
        onContextMenu={(e) => {
          if (!callbacks.onFolderContextMenu) return;
          e.preventDefault();
          callbacks.onFolderContextMenu(folder, { x: e.clientX, y: e.clientY });
        }}
        onDragOver={(e) => {
          native.props.onDragOver(e);
          if (!e.dataTransfer.types.includes(FILE_DND_TYPE)) return;
          e.preventDefault();
          e.stopPropagation();
          // `dropEffect: none` is what makes the CURSOR say no, not just the
          // row — the feedback has to be where the user is looking.
          const allowed = dropState !== "blocked";
          e.dataTransfer.dropEffect = allowed ? "move" : "none";
          if (dropState === "none") {
            // The payload isn't readable during dragover in every browser, so
            // the decision is made once on enter (below) and cached.
            setDropState("ok");
          }
        }}
        onDragEnter={(e) => {
          native.props.onDragEnter(e);
          if (!e.dataTransfer.types.includes(FILE_DND_TYPE)) return;
          const refs = readDrag(e);
          // An empty read means the browser is withholding the payload until
          // drop; assume allowed and let the drop handler be the authority.
          setDropState(refs.length === 0 || canMoveInto(refs, target) ? "ok" : "blocked");
        }}
        onDragLeave={(e) => {
          native.props.onDragLeave(e);
          if (e.currentTarget.contains(e.relatedTarget as Node)) return;
          setDropState("none");
        }}
        onDrop={(e) => {
          native.props.onDrop(e);
          if (!e.dataTransfer.types.includes(FILE_DND_TYPE)) return;
          e.preventDefault();
          e.stopPropagation();
          setDropState("none");
          const refs = readDrag(e);
          const moving = movingRefs(refs, target);
          if (moving.length) callbacks.onDropInto(moving, target);
        }}
      >
        <button
          type="button"
          aria-label={`${open ? "Collapse" : "Expand"} ${folder.name}`}
          disabled={empty}
          onClick={() => onToggle(folder.category, folder.path)}
          className="grid h-6 w-4 shrink-0 place-items-center text-muted-foreground disabled:opacity-0"
        >
          <ChevronRight className={cn("h-3 w-3 transition-transform", open && "rotate-90")} />
        </button>
        <button
          type="button"
          onClick={() => onToggle(folder.category, folder.path)}
          onDoubleClick={() => onToggle(folder.category, folder.path)}
          className="flex min-w-0 flex-1 items-center gap-1.5 py-1 text-left"
        >
          {isRoot ? (
            <span
              aria-hidden
              className="h-[7px] w-[7px] shrink-0 rounded-[2px]"
              style={{ background: CATEGORY_COLOR[folder.category] ?? "" }}
            />
          ) : open ? (
            <FolderOpen
              className="h-3.5 w-3.5 shrink-0"
              style={{ color: folderColor(folder.color) }}
            />
          ) : (
            <FolderIcon
              className="h-3.5 w-3.5 shrink-0"
              style={{ color: folderColor(folder.color) }}
            />
          )}
          <span
            className={cn(
              "truncate text-[12.5px]",
              isRoot ? "font-medium text-foreground/85" : "text-foreground/75",
            )}
          >
            {isRoot ? CATEGORY_LABEL[folder.category] : folder.name}
          </span>
          <span className="ml-auto shrink-0 pl-2 font-mono text-[9px] text-muted-foreground/70">
            {folder.total_count || ""}
          </span>
        </button>
        {callbacks.folderActions?.(folder)}
      </div>

      {open && (
        <div role="group">
          {childrenOf(folder).map((child) => (
            <FolderNode
              key={`${child.category}:${child.path}`}
              folder={child}
              depth={depth + 1}
              isOpen={isOpen}
              onToggle={onToggle}
              ordered={ordered}
              callbacks={callbacks}
            />
          ))}
          {itemsOf(folder).map((item) => (
            <FileRow
              key={refKey(item)}
              item={item}
              folder={folder.path}
              depth={depth + 1}
              ordered={ordered}
              callbacks={callbacks}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FileRow({
  item,
  folder,
  depth,
  ordered,
  callbacks,
}: {
  item: FolderItemOut;
  folder: string;
  depth: number;
  ordered: FileRef[];
  callbacks: FileTreeCallbacks;
}) {
  const ref = toFileRef(item, folder);
  const selected = useFileSelection((s) => s.selected.has(refKey(ref)));
  const cut = useFileSelection(
    (s) => s.clipboard?.mode === "cut" && s.clipboard.refs.some((r) => refKey(r) === refKey(ref)),
  );
  const Icon = itemIcon(item);

  return (
    <div
      role="treeitem"
      aria-selected={selected}
      aria-level={depth + 1}
      tabIndex={-1}
      draggable
      onDragStart={(e) => {
        // Dragging an UNSELECTED row carries only that row; dragging one of a
        // selection carries all of it. Everyone expects this and nobody
        // notices it until it's wrong.
        const refs = refsForGesture(ref);
        if (!useFileSelection.getState().isSelected(ref)) {
          useFileSelection.getState().select(ref);
        }
        e.dataTransfer.setData(FILE_DND_TYPE, JSON.stringify(refs));
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={(e) => applyClick(ref, ordered, { ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey })}
      onDoubleClick={() => callbacks.onOpen(item)}
      onContextMenu={(e) => {
        if (!callbacks.onItemContextMenu) return;
        e.preventDefault();
        // Right-clicking outside the selection selects that row first — acting
        // on an invisible selection is how a context menu deletes the wrong
        // thing.
        if (!useFileSelection.getState().isSelected(ref)) useFileSelection.getState().select(ref);
        callbacks.onItemContextMenu(refsForGesture(ref), { x: e.clientX, y: e.clientY });
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") callbacks.onOpen(item);
      }}
      className={cn(
        "flex cursor-default items-center gap-1.5 rounded py-1 pr-2 transition-colors",
        selected ? "bg-primary/20 text-foreground" : "text-foreground/70 hover:bg-accent/60",
        // A cut file stays visible but reads as "in flight", the way it does in
        // every OS explorer — it has not moved yet.
        cut && "opacity-45",
      )}
      style={{ paddingLeft: `${depth * 11 + 20}px` }}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate text-[12.5px]">{item.title}</span>
      {item.status === "failed" && (
        <span className="ml-auto shrink-0 font-mono text-[9px] uppercase text-destructive/80">
          failed
        </span>
      )}
    </div>
  );
}
